import { useEffect, useRef, useState } from 'react';
import { accountSetupAPI } from '../../services/api';

function jobIsDone(status) { return status === 'completed' || status === 'cancelled'; }

function ResultsTable({ jobId }) {
  const [outcome, setOutcome] = useState('skipped_duplicate_email');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    accountSetupAPI.jobResults(jobId, page, outcome)
      .then((r) => { if (active) setData(r); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [jobId, page, outcome]);

  return (
    <div className="rounded-xl border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <div className="flex gap-1.5">
          {[['skipped_duplicate_email', 'Duplicates'], ['created', 'Created'], ['failed', 'Failed']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => { setOutcome(key); setPage(1); }}
              className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${outcome === key ? 'bg-orange-100 text-orange-700' : 'text-slate-500 hover:bg-slate-100'}`}>
              {label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400">{data?.total?.toLocaleString() ?? '—'}</span>
      </div>
      {loading ? (
        <p className="p-4 text-center text-xs text-slate-400">Loading…</p>
      ) : (
        <>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>
                <th className="px-3 py-2">Member</th><th className="px-3 py-2">Roll No.</th><th className="px-3 py-2">Email</th>
                {outcome === 'created' && <th className="px-3 py-2">Notified via</th>}
              </tr></thead>
              <tbody>{(data?.results || []).map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-700">{r.full_name || `Member #${r.member_id}`}</td>
                  <td className="px-3 py-2 text-slate-500">{r.roll_no || '—'}</td>
                  <td className="px-3 py-2 text-slate-500">{r.email}</td>
                  {outcome === 'created' && (
                    <td className={`px-3 py-2 ${r.notify === 'failed' ? 'text-red-600' : 'text-emerald-600'}`}>{r.notify || '—'}</td>
                  )}
                </tr>
              ))}</tbody>
            </table>
            {!data?.results?.length && <p className="p-4 text-center text-xs text-slate-400">None.</p>}
          </div>
          {(data?.total_pages ?? 1) > 1 && (
            <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs">
              <span>Page {data.page} of {data.total_pages}</span>
              <div className="flex gap-1.5">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border px-2 py-1 disabled:opacity-40">Prev</button>
                <button type="button" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border px-2 py-1 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function AccountSetup() {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [memberPage, setMemberPage] = useState(1);
  const [memberData, setMemberData] = useState(null);
  const [membersLoading, setMembersLoading] = useState(true);
  const [selected, setSelected] = useState({}); // { [memberId]: full_name }

  function loadPreview() {
    setPreviewLoading(true);
    return accountSetupAPI.preview().then((r) => setPreview(r)).catch(() => {}).finally(() => setPreviewLoading(false));
  }

  function loadMembers() {
    setMembersLoading(true);
    return accountSetupAPI.eligibleList(memberPage, query).then((r) => setMemberData(r)).catch(() => {}).finally(() => setMembersLoading(false));
  }

  function loadLatestJob() {
    return accountSetupAPI.jobsList().then((r) => {
      const latest = (r.jobs || [])[0];
      if (latest && !jobIsDone(latest.status)) {
        setJob(latest);
        startPolling(latest.id);
      } else if (latest) {
        setJob(latest);
      }
    }).catch(() => {});
  }

  function startPolling(jobId) {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await accountSetupAPI.jobStatus(jobId);
        setJob(r.job);
        if (jobIsDone(r.job.status)) { clearInterval(pollRef.current); loadPreview(); loadMembers(); }
      } catch { /* keep polling on transient errors */ }
    }, 3000);
  }

  useEffect(() => {
    loadPreview();
    loadLatestJob();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => { loadMembers(); }, [memberPage, query]);
  useEffect(() => { const t = setTimeout(() => { setQuery(search); setMemberPage(1); }, 300); return () => clearTimeout(t); }, [search]);

  const members = memberData?.members || [];
  const selectedIds = Object.keys(selected).map(Number);
  const pageAllSelected = members.length > 0 && members.every((m) => selected[m.id] !== undefined);

  function toggleOne(m) {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[m.id] !== undefined) delete next[m.id]; else next[m.id] = m.full_name;
      return next;
    });
  }
  function togglePage() {
    setSelected((prev) => {
      const next = { ...prev };
      if (pageAllSelected) members.forEach((m) => delete next[m.id]);
      else members.forEach((m) => { next[m.id] = m.full_name; });
      return next;
    });
  }

  async function handleStart(opts) {
    setStarting(true); setError('');
    try {
      const resp = await accountSetupAPI.queue(opts);
      setJob({ id: resp.job_id, total: resp.total, processed: 0, created: 0, skipped: 0, failed: 0, status: 'pending' });
      setSelected({});
      startPolling(resp.job_id);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start.');
    } finally {
      setStarting(false);
    }
  }

  async function handleCancel() {
    if (!job || !window.confirm('Stop this job? Accounts already created stay created — this only stops the rest.')) return;
    try {
      await accountSetupAPI.cancelJob(job.id);
      const r = await accountSetupAPI.jobStatus(job.id);
      setJob(r.job);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to cancel.');
    }
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Account Setup</h2>
        <p className="mt-1 text-sm text-slate-600">
          Creates a portal login account for every member who has an email on file but has never logged in (no account yet) —
          each gets a temporary password by SMS (or email if no phone), and must set a new password on first login.
        </p>
      </div>

      {error && <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {!job || jobIsDone(job.status) ? (
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            {previewLoading ? (
              <p className="text-sm text-slate-400">Checking…</p>
            ) : (
              <>
                <p className="text-sm text-slate-700">
                  <strong>{preview?.eligible?.toLocaleString() ?? 0}</strong> member{preview?.eligible === 1 ? '' : 's'} will get a new account.
                  {preview?.duplicates > 0 && <> <strong>{preview.duplicates.toLocaleString()}</strong> will be skipped (email shared with another member, or already in use).</>}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => handleStart({})} disabled={starting || !preview?.eligible}
                    className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-60">
                    {starting ? 'Starting…' : `Create all ${preview?.eligible?.toLocaleString() || ''} accounts`}
                  </button>
                  {selectedIds.length > 0 && (
                    <button type="button" onClick={() => handleStart({ member_ids: selectedIds })} disabled={starting}
                      className="rounded-xl border border-orange-300 px-3 py-2.5 text-sm font-semibold text-orange-700 hover:bg-orange-50 disabled:opacity-60">
                      Create for {selectedIds.length} selected
                    </button>
                  )}
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">Recommended: pick a few below first (e.g. yourself) and confirm the SMS/email arrives correctly before running the full batch.</p>
              </>
            )}
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-slate-100 p-3">
              <input aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, roll number or email…"
                className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <button type="button" onClick={togglePage} disabled={!members.length}
                className="shrink-0 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                {pageAllSelected ? 'Unselect page' : 'Select page'}
              </button>
            </div>
            {membersLoading ? (
              <p className="p-6 text-center text-sm text-slate-400">Loading…</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500"><tr>
                      <th className="px-4 py-2.5"></th>
                      <th className="px-4 py-2.5 font-semibold">Member</th>
                      <th className="px-4 py-2.5 font-semibold">Roll No.</th>
                      <th className="px-4 py-2.5 font-semibold">Email</th>
                      <th className="px-4 py-2.5 font-semibold">Phone</th>
                    </tr></thead>
                    <tbody>{members.map((m) => (
                      <tr key={m.id} className={`border-t border-slate-100 ${selected[m.id] !== undefined ? 'bg-orange-50/50' : ''}`}>
                        <td className="px-4 py-2.5"><input type="checkbox" checked={selected[m.id] !== undefined} onChange={() => toggleOne(m)} aria-label={`Select ${m.full_name}`} /></td>
                        <td className="px-4 py-2.5 font-medium text-slate-800">{m.full_name}</td>
                        <td className="px-4 py-2.5 text-slate-500">{m.roll_no || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-500">{m.email}</td>
                        <td className="px-4 py-2.5 text-slate-500">{m.phone || '—'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  {!members.length && <p className="p-6 text-center text-sm text-slate-400">No members match.</p>}
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 text-xs text-slate-500">
                  <span>Page {memberData?.page || 1} of {memberData?.total_pages || 1} · {memberData?.total?.toLocaleString() || 0} total</span>
                  <div className="flex gap-2">
                    <button type="button" disabled={memberPage <= 1} onClick={() => setMemberPage((p) => p - 1)} className="rounded-lg border px-2.5 py-1 disabled:opacity-40">Prev</button>
                    <button type="button" disabled={memberPage >= (memberData?.total_pages || 1)} onClick={() => setMemberPage((p) => p + 1)} className="rounded-lg border px-2.5 py-1 disabled:opacity-40">Next</button>
                  </div>
                </div>
              </>
            )}
          </div>

          {job && jobIsDone(job.status) && (
            <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-slate-700">Last run: {job.status === 'completed' ? 'Done' : 'Cancelled'}</span>
                <span className="text-slate-500">{job.processed?.toLocaleString()} / {job.total?.toLocaleString()}</span>
              </div>
              <p className="mt-1.5 text-xs text-slate-500">
                ✓ {job.created?.toLocaleString()} created · ⏭ {job.skipped?.toLocaleString()} skipped (duplicate) · ✕ {job.failed?.toLocaleString()} failed
              </p>
              <div className="mt-3">
                <ResultsTable jobId={job.id} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-700">{job.status === 'running' ? 'Creating accounts…' : 'Queued…'}</span>
            <span className="text-slate-500">{job.processed?.toLocaleString() || 0} / {job.total?.toLocaleString() || 0}</span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${job.total ? ((job.processed || 0) / job.total) * 100 : 0}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            ✓ {job.created?.toLocaleString() || 0} created · ⏭ {job.skipped?.toLocaleString() || 0} skipped · ✕ {job.failed?.toLocaleString() || 0} failed
          </p>
          <p className="mt-1 text-[11px] text-slate-400">This runs in the background — safe to close this page and check back later.</p>
          <button type="button" onClick={handleCancel} className="mt-3 rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}
